import type { Location } from 'history'
import * as React from 'react'
import { useHistory } from 'react-router-dom'

import { useLatest } from './useLatest'

type LeaveAttemptDetails = {
  currentHref: string
  nextHref?: string
  source: 'router' | 'beforeunload'
}

type PageExitDetails = {
  persisted: boolean
}

type UsePageActivityOptions = {
  warnBeforeUnload?: boolean
  confirmBeforeLeave?: boolean
  confirmOnRouteChange?: boolean
  confirmOnReload?: boolean
  confirmMessage?: string
  onLeaveAttempt?(details: LeaveAttemptDetails): void
  onLeaveConfirmed?(details: LeaveAttemptDetails): void
  onLeaveCancelled?(details: LeaveAttemptDetails): void
  onPageExit?(details: PageExitDetails): void
  onActive?: () => void
  onInactive?: () => void
  onVisible?: () => void
  onHidden?: () => void
  onClose?: () => void
  onOnline?: () => void
  onOffline?: () => void
}

export function usePageActivity(options: UsePageActivityOptions = {}) {
  const history = useHistory()
  const latestOptions = useLatest(options)
  const nativeLeaveAttemptRef = React.useRef<LeaveAttemptDetails | null>(null)
  const nativeLeaveCancelledTimeoutRef = React.useRef<number | null>(null)
  const reloadIntentTimeoutRef = React.useRef<number | null>(null)
  const reloadIntentRef = React.useRef(false)
  // A single flag drives both full-page unload prompts and SPA route blockers.
  const shouldConfirmBeforeLeave = React.useCallback(() => {
    return !!(latestOptions.current.confirmBeforeLeave ?? latestOptions.current.warnBeforeUnload)
  }, [latestOptions])
  const shouldConfirmOnRouteChange = React.useCallback(() => {
    return shouldConfirmBeforeLeave() && (latestOptions.current.confirmOnRouteChange ?? true)
  }, [latestOptions, shouldConfirmBeforeLeave])
  const shouldConfirmOnReload = React.useCallback(() => {
    return shouldConfirmBeforeLeave() && (latestOptions.current.confirmOnReload ?? true)
  }, [latestOptions, shouldConfirmBeforeLeave])

  // const [isVisible, setIsVisible] = React.useState(() => {
  //   if (typeof document === 'undefined') return true
  //   return !document.hidden
  // })
  // const [isFocused, setIsFocused] = React.useState(() => {
  //   if (typeof document === 'undefined') return true
  //   return document.hasFocus()
  // })
  // const [isOnline, setIsOnline] = React.useState(() => {
  //   if (typeof navigator === 'undefined') return true
  //   return navigator.onLine
  // })

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return

    // Keep lightweight page activity state in sync with browser-level events.
    const handleVisibilityChange = () => {
      const visible = !document.hidden
      // setIsVisible(visible)

      if (visible) {
        latestOptions.current.onVisible?.()
      } else {
        latestOptions.current.onHidden?.()
      }
    }

    const handleFocus = () => {
      // setIsFocused(true)
      latestOptions.current.onActive?.()
    }

    const handleBlur = () => {
      // Native file pickers blur the window, but the page is still active.
      // Ignoring that transient blur keeps file upload flows from getting reset.
      // if (document.visibilityState === 'visible') {
      //   return
      // }

      // setIsFocused(false)
      latestOptions.current.onInactive?.()
    }

    const handleOnline = () => {
      // setIsOnline(true)
      latestOptions.current.onOnline?.()
    }

    const handleOffline = () => {
      // setIsOnline(false)
      latestOptions.current.onOffline?.()
    }

    const handlePageHide = (event: PageTransitionEvent) => {
      if (nativeLeaveCancelledTimeoutRef.current !== null) {
        window.clearTimeout(nativeLeaveCancelledTimeoutRef.current)
        nativeLeaveCancelledTimeoutRef.current = null
      }

      if (nativeLeaveAttemptRef.current) {
        latestOptions.current.onLeaveConfirmed?.(nativeLeaveAttemptRef.current)
        nativeLeaveAttemptRef.current = null
      }

      latestOptions.current.onPageExit?.({
        persisted: event.persisted,
      })
      latestOptions.current.onClose?.()
    }

    const markReloadIntent = () => {
      reloadIntentRef.current = true

      if (reloadIntentTimeoutRef.current !== null) {
        window.clearTimeout(reloadIntentTimeoutRef.current)
      }

      reloadIntentTimeoutRef.current = window.setTimeout(() => {
        reloadIntentRef.current = false
        reloadIntentTimeoutRef.current = null
      }, 1000)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const key = (event?.key || '').toLowerCase()
      const isKeyboardReload = key === 'f5' || (key === 'r' && (event.ctrlKey || event.metaKey) && !event.altKey)

      if (isKeyboardReload) {
        markReloadIntent()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)
    window.addEventListener('blur', handleBlur)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    window.addEventListener('pagehide', handlePageHide)
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      if (reloadIntentTimeoutRef.current !== null) {
        window.clearTimeout(reloadIntentTimeoutRef.current)
        reloadIntentTimeoutRef.current = null
      }

      reloadIntentRef.current = false
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('blur', handleBlur)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('pagehide', handlePageHide)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [latestOptions])

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    if (!shouldConfirmBeforeLeave()) return

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!shouldConfirmOnReload() && reloadIntentRef.current) {
        return undefined
      }

      const message = latestOptions.current.confirmMessage ?? 'Are you sure you want to leave this page?'
      const leaveAttemptDetails: LeaveAttemptDetails = {
        currentHref: window.location.href,
        source: 'beforeunload',
      }

      nativeLeaveAttemptRef.current = leaveAttemptDetails
      latestOptions.current.onLeaveAttempt?.(leaveAttemptDetails)

      if (nativeLeaveCancelledTimeoutRef.current !== null) {
        window.clearTimeout(nativeLeaveCancelledTimeoutRef.current)
      }

      nativeLeaveCancelledTimeoutRef.current = window.setTimeout(() => {
        if (nativeLeaveAttemptRef.current) {
          latestOptions.current.onLeaveCancelled?.(nativeLeaveAttemptRef.current)
          nativeLeaveAttemptRef.current = null
        }
        nativeLeaveCancelledTimeoutRef.current = null
      }, 0)

      event.preventDefault()
      event.returnValue = message

      return message
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      if (nativeLeaveCancelledTimeoutRef.current !== null) {
        window.clearTimeout(nativeLeaveCancelledTimeoutRef.current)
        nativeLeaveCancelledTimeoutRef.current = null
      }

      nativeLeaveAttemptRef.current = null
      reloadIntentRef.current = false
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [latestOptions, shouldConfirmBeforeLeave, shouldConfirmOnReload])

  React.useEffect(() => {
    if (!shouldConfirmOnRouteChange()) return

    // Block in-app TanStack Router navigation and let the router enable native
    // beforeunload prompts for refresh, close, and external navigation.
    const getLocationHref = (location: Location) => `${location.pathname}${location.search}${location.hash}`

    const unblock = history.block(nextLocation => {
      const currentHref = getLocationHref(history.location)
      const nextHref = getLocationHref(nextLocation)

      if (currentHref === nextHref) return undefined

      if (nativeLeaveCancelledTimeoutRef.current !== null) {
        window.clearTimeout(nativeLeaveCancelledTimeoutRef.current)
        nativeLeaveCancelledTimeoutRef.current = null
      }
      nativeLeaveAttemptRef.current = null

      const message = latestOptions.current.confirmMessage ?? 'Are you sure you want to leave this page?'
      const leaveAttemptDetails: LeaveAttemptDetails = {
        currentHref,
        nextHref,
        source: 'router',
      }

      latestOptions.current.onLeaveAttempt?.(leaveAttemptDetails)

      const isLeaveConfirmed = window.confirm(message)

      if (isLeaveConfirmed) {
        latestOptions.current.onLeaveConfirmed?.(leaveAttemptDetails)
        return undefined
      }

      latestOptions.current.onLeaveCancelled?.(leaveAttemptDetails)
      return false
    })

    return unblock
  }, [history, latestOptions, shouldConfirmOnRouteChange])

  // return {
  //   isVisible,
  //   isFocused,
  //   isOnline,
  //   isActive: isVisible && isFocused,
  // }
}

export type { LeaveAttemptDetails, PageExitDetails, UsePageActivityOptions }
